
import { useState } from "react";
import { useNavigate } from "react-router-dom";
import { MessageCircle, Heart, Star, Zap } from "lucide-react";
import { Button } from "@/components/ui/button";
import { toast } from "@/hooks/use-toast";
import { useAuth } from "@/hooks/useAuth";
import { supabase } from "@/integrations/supabase/client";
import { matchService } from "@/services/matchService";
import TrustActionButton from "@/components/trust/TrustActionButton";
import { useTrust } from "@/hooks/useTrust";
import { useBuzzzActions } from "@/hooks/useBuzzzActions";

interface GprofileActionsProps {
  userId: string;
  displayName: string;
}

const GprofileActions = ({ userId, displayName }: GprofileActionsProps) => {
  const { user } = useAuth();
  const navigate = useNavigate();
  const [isFavorited, setIsFavorited] = useState(false);
  const [loading, setLoading] = useState(false);
  const { trustStats } = useTrust(userId);
  const { sendBuzzz, isLoading: buzzzLoading } = useBuzzzActions();

  const handleMessage = () => {
    if (!user) return;

    try {
      const conversationId = matchService.createConversationId(user.id, userId);
      navigate(`/messages/${conversationId}`);
      
      toast({
        title: "Success",
        description: `Starting conversation with ${displayName}`,
      });
    } catch (error) {
      toast({
        title: "Error",
        description: "Failed to start conversation",
        variant: "destructive"
      });
    }
  };

  const handleFavorite = async () => {
    if (!user || loading) return;

    setLoading(true);
    try {
      if (isFavorited) {
        const { error } = await supabase
          .from('user_favorites')
          .delete()
          .eq('user_id', user.id)
          .eq('favorite_user_id', userId);

        if (error) throw error;
        setIsFavorited(false);
        toast({
          title: "Removed from favorites",
          description: `${displayName} has been removed from your favorites.`,
        });
      } else {
        const { error } = await supabase
          .from('user_favorites')
          .insert({
            user_id: user.id,
            favorite_user_id: userId
          });

        if (error) throw error;
        setIsFavorited(true);
        toast({
          title: "Added to favorites",
          description: `${displayName} has been added to your favorites.`,
        });
      }
    } catch (error) {
      toast({
        title: "Error",
        description: "Failed to update favorites",
        variant: "destructive",
      });
    } finally {
      setLoading(false);
    }
  };

  const handleBuzzz = async () => {
    await sendBuzzz(userId);
  };

  // Don't show own profile actions
  if (user?.id === userId) {
    return null;
  }

  return (
    <div className="px-4 py-6 bg-carbon/50 backdrop-blur-sm">
      <div className="flex gap-3 max-w-sm mx-auto">
        {/* Message Button - Primary */}
        <Button
          onClick={handleMessage}
          className="h-14 px-6 text-base font-semibold bg-neon-red hover:bg-neon-red/80 text-ice-white border-2 border-neon-red hover:border-neon-red/80 transition-all duration-200 hover:scale-105"
        >
          <MessageCircle className="w-5 h-5 mr-2" />
          Message
        </Button>

        {/* Favorite Button */}
        <Button
          onClick={handleFavorite}
          disabled={loading}
          variant="outline"
          className={`h-14 px-4 border-2 transition-all duration-200 hover:scale-105 ${
            isFavorited 
              ? 'bg-toxic-green/20 border-toxic-green text-toxic-green hover:bg-toxic-green/30' 
              : 'bg-ice-white/10 border-ice-white/30 text-ice-white hover:bg-ice-white/20 hover:border-ice-white/50'
          }`}
        >
          {isFavorited ? (
            <Heart className="w-5 h-5 fill-current" />
          ) : (
            <Star className="w-5 h-5" />
          )}
        </Button>

        {/* Buzzz Button */}
        <Button
          onClick={handleBuzzz}
          disabled={buzzzLoading}
          variant="outline"
          className="h-14 px-4 border-2 bg-neon-yellow/20 border-neon-yellow text-neon-yellow hover:bg-neon-yellow/30 hover:border-neon-yellow/80 transition-all duration-200 hover:scale-105"
        >
          <Zap className="w-5 h-5" />
        </Button>
      </div>

      {/* Trust Action Button */}
      {trustStats && (
        <div className="mt-4 flex justify-center">
          <TrustActionButton
            userId={userId}
            displayName={displayName}
            canTrustOthers={trustStats.can_trust_others}
            hasTrustedTarget={trustStats.has_trusted_target}
            variant="outline"
            className="bg-ice-white/10 border-ice-white/30 text-ice-white hover:bg-ice-white/20 hover:border-ice-white/50"
          />
        </div>
      )}
    </div>
  );
};

export default GprofileActions;
